import './toolbar.css';
import type { DicomJson, InstanceRef, SeriesRef } from '../dicomweb';
import type { ViewerShell } from '../shell';
import type { Viewport } from '../viewport';
import { timedFetch } from '../net';

/**
 * Modal dialogs (spec section 1.4): "Guía rápida" (help + hotkeys, without any vendor or regulatory
 * text), "Metadatos DICOM" (tag table) and "Descargar imagen" (PNG with measurements).
 * All are role="dialog", close on Esc, and keep Tab focus inside.
 */

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const MAX_VALUE_LENGTH = 256;
const DOWNLOAD_MIME = 'image/png';
const BURN_IN_MIN_FONT_PX = 12;
const BURN_IN_FONT_RATIO = 40;
const BURN_IN_MARGIN_RATIO = 0.6;
const DICOM_JSON = 'application/dicom+json';

export interface DialogButton {
  label: string;
  primary?: boolean;
  onClick(close: () => void): void;
}

export interface DialogOptions {
  title: string;
  className: string;
  buttons: DialogButton[];
  /** Any click inside the dialog closes it (help: "Haga click para cerrar este diálogo"). */
  closeOnClick?: boolean;
}

/** True while any of these dialogs is open (the keyboard map stays quiet). */
export function isDialogOpen(): boolean {
  return document.querySelector('.cmd-dialog-backdrop') !== null;
}

function trapFocus(dialog: HTMLElement, event: KeyboardEvent): void {
  const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute('disabled'));
  if (focusables.length === 0) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/** Opens a modal; returns its body element and a close function. */
export function openDialog(shell: ViewerShell, options: DialogOptions): { body: HTMLElement; close: () => void; dialog: HTMLElement } {
  document.querySelectorAll('.cmd-dialog-backdrop').forEach((el) => el.remove());
  const previousFocus = document.activeElement as HTMLElement | null;
  const backdrop = document.createElement('div');
  backdrop.className = 'cmd-dialog-backdrop';
  const dialog = document.createElement('div');
  dialog.className = `cmd-dialog ${options.className}`;
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.tabIndex = -1;
  const titleId = `${options.className}-title`;
  dialog.setAttribute('aria-labelledby', titleId);
  const heading = document.createElement('h2');
  heading.className = 'cmd-dialog-title';
  heading.id = titleId;
  heading.textContent = options.title;
  const body = document.createElement('div');
  body.className = 'cmd-dialog-body';
  const footer = document.createElement('div');
  footer.className = 'cmd-dialog-actions';
  const close = () => {
    backdrop.remove();
    document.removeEventListener('keydown', onKey, true);
    previousFocus?.focus?.({ preventScroll: true });
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'Tab') {
      trapFocus(dialog, event);
    }
  };
  for (const spec of options.buttons) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `cmd-dialog-button${spec.primary ? ' primary' : ''}`;
    button.textContent = spec.label;
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      spec.onClick(close);
    });
    footer.appendChild(button);
  }
  dialog.append(heading, body, footer);
  backdrop.appendChild(dialog);
  backdrop.addEventListener('mousedown', (event) => {
    if (event.target === backdrop) close();
  });
  if (options.closeOnClick) dialog.addEventListener('click', close);
  document.addEventListener('keydown', onKey, true);
  shell.regions.overlayLayer.appendChild(backdrop);
  (dialog.querySelector<HTMLElement>('.cmd-dialog-button.primary') ?? dialog.querySelector<HTMLElement>(FOCUSABLE) ?? dialog).focus();
  return { body, close, dialog };
}

export function paragraph(parent: HTMLElement, text: string, className = ''): HTMLParagraphElement {
  const p = document.createElement('p');
  if (className) p.className = className;
  p.textContent = text;
  parent.appendChild(p);
  return p;
}

/** A message box (spec modal-dlg / blocking dialogs): title, text and one closing button. */
export function messageDialog(shell: ViewerShell, title: string, text: string, className = 'message-dialog', buttonLabel = 'Cerrar'): void {
  const { body } = openDialog(shell, { title, className, buttons: [{ label: buttonLabel, primary: true, onClick: (close) => close() }] });
  paragraph(body, text, 'message-text');
}

/** Asks a question in page; resolves true when the user picks `confirmLabel` (Esc / backdrop = false). */
export function confirmDialog(shell: ViewerShell, options: { title: string; text: string; confirmLabel: string; cancelLabel: string; className: string }): Promise<boolean> {
  return new Promise((resolve) => {
    let answered = false;
    const answer = (value: boolean, close: () => void) => {
      answered = true;
      close();
      resolve(value);
    };
    const { body, dialog } = openDialog(shell, {
      title: options.title,
      className: options.className,
      buttons: [
        { label: options.cancelLabel, onClick: (close) => answer(false, close) },
        { label: options.confirmLabel, primary: true, onClick: (close) => answer(true, close) },
      ],
    });
    paragraph(body, options.text, 'message-text');
    // Esc or a click on the backdrop close without an answer: that is a "no".
    const observer = new MutationObserver(() => {
      if (!dialog.isConnected) {
        observer.disconnect();
        if (!answered) resolve(false);
      }
    });
    observer.observe(shell.regions.overlayLayer, { childList: true });
  });
}

// --- Guía rápida -------------------------------------------------------------------------------

const HELP_SENTENCES = [
  'Haga click sobre una serie en la lista en la izquierda y seleccione la ubicación para visualizar.',
  'Use la rueda del mouse, la herramienta avanzar o las flechas del teclado para moverse en la serie.',
  'Haga doble click en una imagen para modo de pantalla completa, haga doble click de nuevo para regresar.',
  'Utilice el click derecho para obtener acceso rápido a las herramientas.',
  'Mantenga oprimido el botón derecho del mouse para acercar o alejar, mantenga oprimido el botón del medio para desplazar.',
  'Puede contraer o expandir estudios en la lista haciendo clic en el nombre del estudio.',
];

/** Keyboard map of spec section 8, as the help dialog documents it. */
export const HOTKEYS: Array<[keys: string, action: string]> = [
  ['→ / ↑ / U', 'Siguiente corte'],
  ['← / ↓ / Y', 'Corte anterior'],
  ['Ctrl + ← → ↑ ↓', 'Alinear la imagen a la izquierda, derecha, arriba o abajo'],
  ['P', 'Mover la imagen'],
  ['Z', 'Magnificar o alejar'],
  ['W', 'Cambiar ventana y nivel de ventana'],
  ['S', 'Avanzar en la serie'],
  ['M', 'Magnifica zona de interés'],
  ['A', 'Medir ángulos'],
  ['L', 'Medir distancia con una línea'],
  ['B', 'ROI Elíptica'],
  ['C', 'Medir ángulos de Cobb'],
  ['Ctrl + C', 'Copiar todas las imágenes al portapapeles'],
  ['D', 'Medir densidad de un punto'],
  ['E', 'Borrar mediciones'],
  ['T', 'Triangulación'],
  ['R', 'Reiniciar ventana y posición'],
  ['H', 'Ocultar información DICOM'],
  ['I', 'Invertir polaridad de la imagen'],
  ['F', 'Reproducir o pausar el cine'],
  ['Q', 'Borrar las mediciones del corte actual'],
  ['X / V', 'Serie anterior / siguiente'],
  ['Esc', 'Cerrar este diálogo'],
  ['1 … 9, 0', 'Ventanas predeterminadas (1 = ventana original de la imagen)'],
];

/** "Guía rápida": usage sentences plus the "Accesos con teclado" table. */
export function openHelpDialog(shell: ViewerShell): void {
  const { body } = openDialog(shell, {
    title: 'Guía rápida',
    className: 'help-dialog',
    closeOnClick: true,
    buttons: [{ label: 'Cerrar', primary: true, onClick: (close) => close() }],
  });
  const list = document.createElement('ul');
  list.className = 'help-sentences';
  for (const sentence of HELP_SENTENCES) {
    const li = document.createElement('li');
    li.textContent = sentence;
    list.appendChild(li);
  }
  body.appendChild(list);
  const heading = document.createElement('h3');
  heading.textContent = 'Accesos con teclado';
  body.appendChild(heading);
  const table = document.createElement('table');
  table.className = 'hotkey-table';
  for (const [keys, action] of HOTKEYS) {
    const row = table.insertRow();
    const keyCell = row.insertCell();
    keyCell.className = 'hotkey-keys';
    keyCell.textContent = keys;
    row.insertCell().textContent = action;
  }
  body.appendChild(table);
  paragraph(body, 'Haga click para cerrar este diálogo', 'help-footer');
}

// --- Metadatos DICOM ---------------------------------------------------------------------------

/** Common attribute names (keyword form, as DICOM PS3.6 lists them). */
const TAG_NAMES: Record<string, string> = {
  '00020010': 'TransferSyntaxUID',
  '00080005': 'SpecificCharacterSet',
  '00080008': 'ImageType',
  '00080012': 'InstanceCreationDate',
  '00080013': 'InstanceCreationTime',
  '00080016': 'SOPClassUID',
  '00080018': 'SOPInstanceUID',
  '00080020': 'StudyDate',
  '00080021': 'SeriesDate',
  '00080022': 'AcquisitionDate',
  '00080023': 'ContentDate',
  '00080030': 'StudyTime',
  '00080031': 'SeriesTime',
  '00080032': 'AcquisitionTime',
  '00080033': 'ContentTime',
  '00080050': 'AccessionNumber',
  '00080060': 'Modality',
  '00080070': 'Manufacturer',
  '00080080': 'InstitutionName',
  '00080090': 'ReferringPhysicianName',
  '00081010': 'StationName',
  '00081030': 'StudyDescription',
  '0008103E': 'SeriesDescription',
  '00081090': 'ManufacturerModelName',
  '00100010': 'PatientName',
  '00100020': 'PatientID',
  '00100030': 'PatientBirthDate',
  '00100040': 'PatientSex',
  '00101010': 'PatientAge',
  '00101020': 'PatientSize',
  '00101030': 'PatientWeight',
  '00180015': 'BodyPartExamined',
  '00180040': 'CineRate',
  '00180050': 'SliceThickness',
  '00180060': 'KVP',
  '00180080': 'RepetitionTime',
  '00180081': 'EchoTime',
  '00180086': 'EchoNumbers',
  '00180087': 'MagneticFieldStrength',
  '00180088': 'SpacingBetweenSlices',
  '00181020': 'SoftwareVersions',
  '00181030': 'ProtocolName',
  '00181063': 'FrameTime',
  '00181150': 'ExposureTime',
  '00181151': 'XRayTubeCurrent',
  '00181152': 'Exposure',
  '00181164': 'ImagerPixelSpacing',
  '00181314': 'FlipAngle',
  '00185100': 'PatientPosition',
  '00185101': 'ViewPosition',
  '00082144': 'RecommendedDisplayFrameRate',
  '0020000D': 'StudyInstanceUID',
  '0020000E': 'SeriesInstanceUID',
  '00200010': 'StudyID',
  '00200011': 'SeriesNumber',
  '00200012': 'AcquisitionNumber',
  '00200013': 'InstanceNumber',
  '00200020': 'PatientOrientation',
  '00200032': 'ImagePositionPatient',
  '00200037': 'ImageOrientationPatient',
  '00200052': 'FrameOfReferenceUID',
  '00200060': 'Laterality',
  '00200062': 'ImageLaterality',
  '00201041': 'SliceLocation',
  '00201209': 'NumberOfSeriesRelatedInstances',
  '00280002': 'SamplesPerPixel',
  '00280004': 'PhotometricInterpretation',
  '00280006': 'PlanarConfiguration',
  '00280008': 'NumberOfFrames',
  '00280010': 'Rows',
  '00280011': 'Columns',
  '00280030': 'PixelSpacing',
  '00280100': 'BitsAllocated',
  '00280101': 'BitsStored',
  '00280102': 'HighBit',
  '00280103': 'PixelRepresentation',
  '00281050': 'WindowCenter',
  '00281051': 'WindowWidth',
  '00281052': 'RescaleIntercept',
  '00281053': 'RescaleSlope',
  '00281054': 'RescaleType',
  '00281055': 'WindowCenterWidthExplanation',
  '00281056': 'VOILUTFunction',
  '00282110': 'LossyImageCompression',
  '00282112': 'LossyImageCompressionRatio',
  '00282114': 'LossyImageCompressionMethod',
  '7FE00010': 'PixelData',
};

interface MetadataRow {
  tag: string;
  name: string;
  vr: string;
  value: string;
}

function formatTag(tag: string): string {
  return `(${tag.slice(0, 4)},${tag.slice(4, 8)})`;
}

function tagName(tag: string): string {
  const upper = tag.toUpperCase();
  if (TAG_NAMES[upper]) return TAG_NAMES[upper];
  const group = parseInt(upper.slice(0, 4), 16);
  if (upper.slice(4) === '0000') return 'GroupLength';
  return group % 2 === 1 ? 'Private' : '';
}

function formatValue(entry: { vr: string; Value?: unknown[]; InlineBinary?: string; BulkDataURI?: string }): string {
  if (entry.InlineBinary !== undefined || entry.BulkDataURI !== undefined) return '[datos binarios]';
  const values = entry.Value ?? [];
  const text = values
    .map((value) => {
      if (value && typeof value === 'object' && 'Alphabetic' in value) return String((value as { Alphabetic?: string }).Alphabetic ?? '');
      return String(value);
    })
    .join('\\');
  return text.length > MAX_VALUE_LENGTH ? `${text.slice(0, MAX_VALUE_LENGTH)}…` : text;
}

/** Flattens DICOM JSON into table rows; sequence items are listed under their parent with ">" marks. */
export function metadataRows(json: DicomJson, depth = 0): MetadataRow[] {
  const rows: MetadataRow[] = [];
  const prefix = '>'.repeat(depth);
  for (const tag of Object.keys(json).sort()) {
    const entry = json[tag] as ({ vr: string; Value?: unknown[]; InlineBinary?: string; BulkDataURI?: string }) | undefined;
    if (!entry) continue;
    if (entry.vr === 'SQ') {
      const items = (entry.Value ?? []) as DicomJson[];
      rows.push({ tag: `${prefix}${formatTag(tag)}`, name: tagName(tag), vr: 'SQ', value: `${items.length} elemento(s)` });
      items.forEach((item) => rows.push(...metadataRows(item, depth + 1)));
      continue;
    }
    rows.push({ tag: `${prefix}${formatTag(tag)}`, name: tagName(tag), vr: entry.vr, value: formatValue(entry) });
  }
  return rows;
}

/** The instance (and frame) shown at stack position `index`. */
export function instanceAt(series: SeriesRef, index: number): InstanceRef | undefined {
  let remaining = index;
  for (const instance of series.instances) {
    if (remaining < instance.numberOfFrames) return instance;
    remaining -= instance.numberOfFrames;
  }
  return series.instances[series.instances.length - 1];
}

/** WADO-RS metadata of one instance (already in memory in fast mode). */
export async function fetchInstanceMetadata(shell: ViewerShell, series: SeriesRef, instance: InstanceRef): Promise<DicomJson> {
  if (instance.metadata) return instance.metadata;
  const url = `${shell.config.dicomwebRoot}/studies/${series.studyInstanceUID}/series/${series.seriesInstanceUID}/instances/${instance.sopInstanceUID}/metadata`;
  const response = await timedFetch(url, { headers: { Accept: DICOM_JSON, ...shell.ctx.client.requestHeaders() }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`metadata ${response.status}`);
  const rows = (await response.json()) as DicomJson[];
  if (!rows[0]) throw new Error('empty metadata');
  return rows[0];
}

function renderMetadataTable(rows: MetadataRow[]): HTMLTableElement {
  const table = document.createElement('table');
  table.className = 'metadata-table';
  const head = table.createTHead().insertRow();
  for (const label of ['Etiqueta', 'Nombre del Atributo', 'VR', 'Valor']) {
    const th = document.createElement('th');
    th.textContent = label;
    head.appendChild(th);
  }
  const tbody = table.createTBody();
  for (const row of rows) {
    const tr = tbody.insertRow();
    for (const value of [row.tag, row.name, row.vr, row.value]) tr.insertCell().textContent = value;
  }
  return table;
}

/** "Metadatos DICOM": tag table of the instance on screen in `viewport`. */
export async function openMetadataDialog(shell: ViewerShell, viewport: Viewport): Promise<void> {
  const stack = viewport.currentStack;
  if (!stack) return;
  const { body } = openDialog(shell, {
    title: 'Metadatos DICOM',
    className: 'metadata-dialog',
    buttons: [{ label: 'Cancelar', onClick: (close) => close() }],
  });
  const status = paragraph(body, 'Cargando ...', 'metadata-status');
  const instance = instanceAt(stack.series, viewport.currentIndex() ?? 0);
  if (!instance) {
    status.textContent = 'No se pudo obtener metadata valida para la serie';
    return;
  }
  try {
    const json = await fetchInstanceMetadata(shell, stack.series, instance);
    status.remove();
    const scroller = document.createElement('div');
    scroller.className = 'metadata-scroll';
    scroller.appendChild(renderMetadataTable(metadataRows(json)));
    body.appendChild(scroller);
  } catch (error) {
    console.warn('[viewer] metadata failed', error);
    status.textContent = 'No se pudo obtener metadata valida para la serie';
  }
}

// --- Descargar imagen --------------------------------------------------------------------------

/** Copy of a viewport's cornerstone canvas: the image plus the measurements drawn on it. */
export function snapshotViewport(viewport: Viewport): HTMLCanvasElement | null {
  const source = window.cornerstone.getEnabledElement(viewport.element).canvas;
  if (!source || source.width === 0 || source.height === 0) return null;
  const copy = document.createElement('canvas');
  copy.width = source.width;
  copy.height = source.height;
  copy.getContext('2d')?.drawImage(source, 0, 0);
  return copy;
}

function patientLines(viewport: Viewport): string[] {
  const study = viewport.study;
  const series = viewport.currentStack?.series;
  const date = /^\d{8}$/.test(study.studyDate)
    ? `${study.studyDate.slice(0, 4)}-${study.studyDate.slice(4, 6)}-${study.studyDate.slice(6, 8)}`
    : study.studyDate;
  return [
    study.patientName,
    study.patientId ? `ID: ${study.patientId}` : '',
    date,
    series?.description ?? '',
  ].filter(Boolean);
}

function burnIn(canvas: HTMLCanvasElement, lines: string[]): void {
  const context = canvas.getContext('2d');
  if (!context) return;
  const fontPx = Math.max(BURN_IN_MIN_FONT_PX, Math.round(canvas.width / BURN_IN_FONT_RATIO));
  const margin = Math.round(fontPx * BURN_IN_MARGIN_RATIO);
  context.font = `${fontPx}px Arial, Helvetica, sans-serif`;
  context.textBaseline = 'top';
  context.fillStyle = '#ffffff';
  context.shadowColor = '#000000';
  context.shadowBlur = 2;
  lines.forEach((line, i) => context.fillText(line, margin, margin + i * fontPx * 1.25));
}

function downloadName(viewport: Viewport): string {
  const series = viewport.currentStack?.series;
  const base = [series?.description || series?.modality || 'imagen', String((viewport.currentIndex() ?? 0) + 1)]
    .join('_')
    .replace(/[^\p{L}\p{N}_-]+/gu, '_');
  return `${base}.png`;
}

function saveCanvas(canvas: HTMLCanvasElement, filename: string): void {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, DOWNLOAD_MIME);
}

/** "Descargar imagen": PNG of the viewport with its measurements, optionally with patient data burned in. */
export function openDownloadDialog(shell: ViewerShell, viewport: Viewport): void {
  const render = (withPatient: boolean): HTMLCanvasElement | null => {
    const canvas = snapshotViewport(viewport);
    if (canvas && withPatient) burnIn(canvas, patientLines(viewport));
    return canvas;
  };
  let withPatient = false;
  const { body } = openDialog(shell, {
    title: 'Descargar imagen',
    className: 'download-dialog',
    buttons: [
      { label: 'CANCELAR', onClick: (close) => close() },
      {
        label: 'DESCARGAR',
        primary: true,
        onClick: (close) => {
          const canvas = render(withPatient);
          if (canvas) saveCanvas(canvas, downloadName(viewport));
          close();
        },
      },
    ],
  });
  paragraph(body, 'Sección para descargar la imagen de la instancia actual.');
  paragraph(body, 'Nota: La imagen a descargar está en formato .png, el formato png se elige por su alta calidad de imagen.', 'download-note');
  paragraph(body, 'Permitir añadir información del paciente a la imagen.');
  const label = document.createElement('label');
  label.className = 'download-patient';
  const checkbox = document.createElement('input');
  checkbox.type = 'checkbox';
  checkbox.className = 'download-patient-checkbox';
  label.append(checkbox, document.createTextNode(' Añadir datos paciente'));
  body.appendChild(label);
  paragraph(body, 'Vista previa de la imagen', 'download-preview-title');
  const preview = document.createElement('img');
  preview.className = 'download-preview';
  preview.alt = 'Vista previa de la imagen';
  body.appendChild(preview);
  const refresh = () => {
    const canvas = render(withPatient);
    if (canvas) preview.src = canvas.toDataURL(DOWNLOAD_MIME);
  };
  checkbox.addEventListener('change', () => {
    withPatient = checkbox.checked;
    refresh();
  });
  refresh();
}
